import React, { useCallback } from "react";
import { ReactSortable } from "react-sortablejs";
import PropTypes from "prop-types";

import { useBooleanState } from "hooks";

import { useDragDropContext } from "scenes/Hubly/components/Workspace/components/WorkflowContainer/components/WorkflowEditor/hooks";

import {
  ConditionsGroupDetails,
  ConditionsGroupViewLayout,
  Menu,
  TaskConditionModal,
  useConditionModalActions,
} from "./components";

export function ConditionsGroupView({
  conditionsGroupId,
  conditions,
  items,
  itemType,
  children,
  workflowId,
  disabled,
  showIcon,
  withDraggable,
  onDragged,
  onConfigChange,
  onOrderModified,
}) {
  const onDone = (data) => {
    return onConfigChange(
      {
        idsToUpdate: items.map(({ id }) => {
          return id;
        }),
        updatedValue: data,
      },
      "editMultiple"
    );
  };

  const [isOpen, open, close] = useBooleanState();
  const { remove, isLoading } = useConditionModalActions(onDone);
  const [{ disabled: disableDnd }] = useDragDropContext();

  const getConditionModalSubtitle = useCallback(() => {
    if (itemType === "task") {
      return items.length > 1 ? "Multiple Tasks" : items[0]?.title || "";
    }

    return items.length > 1
      ? `This Workflow: ${
          items[0]?.workflow?.name || ""
        } - Multiple Next Workflows`
      : `This Workflow: ${items[0]?.workflow?.name || ""} - Next Workflow: ${
          items[0]?.nextWorkflow?.name || ""
        }`;
  }, [items, itemType]);

  if (!items?.length) return null;

  return (
    <div id={conditionsGroupId}>
      <ConditionsGroupViewLayout
        itemType={itemType}
        disabled={disabled}
        onClick={disabled ? () => {} : open}
        withDraggable={withDraggable}
        showIcon={showIcon}
        conditionDetails={<ConditionsGroupDetails conditions={conditions} />}
        popupMenu={
          !disabled && (
            <Menu>
              {({ closeMenu }) => {
                return (
                  <React.Fragment>
                    <Menu.Button
                      content="Edit Condition"
                      icon="edit"
                      disabled={disabled}
                      onClick={() => {
                        closeMenu();
                        open();
                      }}
                    />
                    <Menu.Button
                      content="Delete Condition"
                      icon="delete"
                      disabled={disabled}
                      loading={isLoading}
                      onClick={async (e) => {
                        e.stopPropagation();
                        await remove(conditionsGroupId);
                        closeMenu();
                      }}
                    />
                  </React.Fragment>
                );
              }}
            </Menu>
          )
        }
      >
        {disabled ? (
          children(items)
        ) : (
          <ReactSortable
            key={conditionsGroupId}
            group={`${itemType}Groups-${workflowId}`}
            list={items}
            animation={100}
            handle={`.${itemType}-drag-handle`}
            ghostClass="hubly-dnd-ghost"
            setList={onOrderModified}
            disabled={disabled || disableDnd}
            onStart={(e) => {
              onDragged(true, e);
            }}
            onEnd={(e) => {
              onDragged(false, e);
            }}
            forceFallback
          >
            {children(items)}
          </ReactSortable>
        )}
      </ConditionsGroupViewLayout>

      {isOpen && (
        <TaskConditionModal
          workflowId={workflowId}
          disabled={disabled}
          itemType={itemType}
          conditionsGroup={{
            id: conditionsGroupId,
            conditions: conditions,
          }}
          order={items[0].order}
          isCreated
          subtitle={getConditionModalSubtitle()}
          onDone={onDone}
          onClose={close}
        />
      )}
    </div>
  );
}

ConditionsGroupView.propTypes = {
  workflowId: PropTypes.string.isRequired,
  conditionsGroupId: PropTypes.string.isRequired,
  onConfigChange: PropTypes.func.isRequired,
  onOrderModified: PropTypes.func.isRequired,
  itemType: PropTypes.oneOf(["task", "nextWorkflow"]),
  items: PropTypes.arrayOf(PropTypes.object),
  conditions: PropTypes.arrayOf(PropTypes.object),
  onDragged: PropTypes.func,
  children: PropTypes.func,
  disabled: PropTypes.bool,
  withDraggable: PropTypes.bool,
  showIcon: PropTypes.bool,
};

ConditionsGroupView.defaultProps = {
  items: [],
  conditions: [],
  disabled: false,
  onDragged: () => {},
  children: () => {},
  withDraggable: false,
  itemType: "task",
  showIcon: true,
};

export default ConditionsGroupView;
